import client from './client';

/** Что изменится / что изменилось по одному набору. */
export interface SyncStats {
  dataset: string;
  fetched: number;
  existing_active: number;
  added: number;
  changed: number;
  missing: number;
  unchanged: number;
  duplicates: { key: string; n: number; label: string }[];
  added_samples: { key: string; label: string }[];
  changed_samples: { key: string; label: string; changes: Record<string, [any, any]> }[];
  missing_samples: { key: string; label: string }[];
  changed_fields: Record<string, number>;
  refused: boolean;
  refuse_reason: string | null;
  written: Record<string, any>;
  backup_table: string | null;
  backup_file: string | null;
  restore_command: string | null;
  notes: string[];
}

export type RunStatus = 'running' | 'success' | 'failed' | 'refused';
export type RunMode = 'dry' | 'apply';

export interface SyncRun {
  id: number;
  dataset: string;
  dataset_title: string;
  mode: RunMode;
  status: RunStatus;
  started_at: string;
  finished_at: string | null;
  started_by_name: string | null;
  stats: SyncStats | null;
  backup_path: string | null;
  log?: string | null;
  error: string | null;
}

export interface RunRef {
  id: number;
  status: RunStatus;
  started_at: string;
  finished_at: string | null;
}

export interface DatasetCard {
  key: string;
  title: string;
  subtitle: string;
  table: string | null;
  rows: number | null;
  expected_min: number | null;
  danger: string | null;
  last_dry: RunRef | null;
  last_apply: RunRef | null;
}

/** Что идёт прямо сейчас. `progress` — последняя строка лога прогона. */
export interface RunningNow {
  id: number;
  dataset: string;
  dataset_title: string;
  mode: RunMode;
  started_at: string;
  progress: string | null;
}

export type BatchStatus = 'running' | 'success' | 'partial' | 'failed';

/** Строка сводного отчёта пачки — один набор. */
export interface BatchItem {
  key: string;
  title: string;
  run_id: number;
  status: RunStatus;
  error: string | null;
  backup: string | null;
  fetched: number;
  added: number;
  changed: number;
  closed: number;
}

export interface BatchTotals {
  fetched: number;
  added: number;
  changed: number;
  closed: number;
  ok: number;
  refused: number;
  failed: number;
}

export interface SyncBatch {
  id: number;
  mode: RunMode;
  status: BatchStatus;
  started_at: string;
  finished_at: string | null;
  total: number;
  done: number;
  current_dataset: string | null;
  current_title: string | null;
  started_by_name: string | null;
  stats: { items?: BatchItem[]; totals?: BatchTotals } | null;
  error: string | null;
  runs?: SyncRun[];
}

export interface BatchRef {
  id: number;
  mode: RunMode;
  status: BatchStatus;
  started_at: string;
  finished_at: string | null;
  total: number;
  done: number;
}

export interface DatasetsResponse {
  items: DatasetCard[];
  running: RunningNow | null;
  batch: SyncBatch | null;
  last_batch_dry: BatchRef | null;
  last_batch_apply: BatchRef | null;
  batch_datasets: string[];
  dry_run_ttl_hours: number;
}

export const datasyncApi = {
  datasets: async (): Promise<DatasetsResponse> => {
    const { data } = await client.get('/api/admin/datasync/datasets');
    return data;
  },
  start: async (dataset: string, mode: RunMode) => {
    const { data } = await client.post('/api/admin/datasync/runs', { dataset, mode });
    return data as { id: number };
  },
  runs: async (dataset?: string, limit = 20): Promise<{ items: SyncRun[] }> => {
    const { data } = await client.get('/api/admin/datasync/runs', {
      params: { dataset, limit },
    });
    return data;
  },
  run: async (id: number): Promise<SyncRun> => {
    const { data } = await client.get(`/api/admin/datasync/runs/${id}`);
    return data;
  },
  startBatch: async (mode: RunMode) => {
    const { data } = await client.post('/api/admin/datasync/batches', { mode });
    return data as { id: number; total: number };
  },
  batches: async (limit = 10): Promise<{ items: SyncBatch[] }> => {
    const { data } = await client.get('/api/admin/datasync/batches', { params: { limit } });
    return data;
  },
  batch: async (id: number): Promise<SyncBatch> => {
    const { data } = await client.get(`/api/admin/datasync/batches/${id}`);
    return data;
  },
};
